import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { ColorDot, PersonName } from '../components/common/PersonColor';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMins(total) {
  if (!total) return '0:00';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ── RolesTab ──────────────────────────────────────────────────────────────────

function RolesTab({ roles, users, onRolesChange }) {
  const [drafts, setDrafts] = useState({});
  const [saved, setSaved] = useState({});
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  const draft = (id) => drafts[id] || {};

  function setField(id, field, value) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));
  }

  async function saveRole(role) {
    const d = draft(role.id);
    const payload = {
      name: d.name !== undefined ? d.name : role.name,
      weekly_hours_target: d.weekly_hours_target !== undefined ? Number(d.weekly_hours_target) : role.weekly_hours_target,
      holder_user_id: d.holder_user_id !== undefined ? d.holder_user_id : (role.holder_user_id ?? ''),
    };
    try {
      await api.put(`/om/roles/${role.id}`, payload);
      setSaved((s) => ({ ...s, [role.id]: true }));
      setTimeout(() => setSaved((s) => { const n = { ...s }; delete n[role.id]; return n; }), 2000);
      onRolesChange();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler beim Speichern');
    }
  }

  async function addRole() {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post('/om/roles', { name: newName.trim() });
      setNewName('');
      toast.success('Rolle erstellt');
      onRolesChange();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler');
    } finally {
      setAdding(false);
    }
  }

  async function toggleActive(role) {
    const endpoint = role.is_active ? 'deactivate' : 'activate';
    try {
      await api.post(`/om/roles/${role.id}/${endpoint}`);
      onRolesChange();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler');
    }
    setConfirmDeactivate(null);
  }

  const active = roles.filter((r) => r.is_active);
  const inactive = roles.filter((r) => !r.is_active);

  const rowStyle = {
    display: 'flex', gap: '10px', alignItems: 'flex-end',
    flexWrap: 'wrap', padding: '14px 0', borderBottom: '1px solid var(--border)',
  };
  const labelStyle = { fontSize: '0.75rem', color: 'var(--text-light)', marginBottom: '4px', display: 'block' };

  function RoleRow({ role }) {
    const d = draft(role.id);
    const nameVal = d.name !== undefined ? d.name : role.name;
    const hoursVal = d.weekly_hours_target !== undefined ? d.weekly_hours_target : role.weekly_hours_target;
    const holderVal = d.holder_user_id !== undefined ? d.holder_user_id : (role.holder_user_id ?? '');

    return (
      <div style={rowStyle}>
        <div style={{ flex: '2 1 180px', minWidth: '140px' }}>
          <label style={labelStyle}>Bezeichnung</label>
          <input
            className="form-input"
            value={nameVal}
            onChange={(e) => setField(role.id, 'name', e.target.value)}
            placeholder="Rollenname"
          />
        </div>
        <div style={{ flex: '2 1 180px', minWidth: '140px' }}>
          <label style={labelStyle}>Verantwortlich</label>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {holderVal !== '' && holderVal != null && (
            <ColorDot userId={Number(holderVal)} size={10} style={{ position: 'absolute', left: '11px', pointerEvents: 'none' }} />
          )}
          <select
            className="form-input"
            style={holderVal !== '' && holderVal != null ? { paddingLeft: '28px' } : undefined}
            value={holderVal}
            onChange={(e) => setField(role.id, 'holder_user_id', e.target.value)}
          >
            <option value="">— none —</option>
            {users.filter((u) => u.is_active).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
            {/* Keep a deactivated holder visible so the stored value is not hidden */}
            {role.holder_user_id && !users.some((u) => u.is_active && u.id === role.holder_user_id) && (
              <option value={role.holder_user_id}>
                {role.holder_name || `User #${role.holder_user_id}`} (inactive)
              </option>
            )}
          </select>
          </div>
        </div>
        <div style={{ flex: '1 1 100px', minWidth: '80px' }}>
          <label style={labelStyle}>Soll h/Woche</label>
          <input
            className="form-input"
            type="number"
            min="0"
            step="0.5"
            value={hoursVal}
            onChange={(e) => setField(role.id, 'weekly_hours_target', e.target.value)}
            style={{ width: '80px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '6px', paddingBottom: '2px', flexShrink: 0 }}>
          <button
            className="btn btn-primary"
            style={{ padding: '8px 16px' }}
            onClick={() => saveRole(role)}
          >
            {saved[role.id] ? '✓' : 'Speichern'}
          </button>
          {role.is_active ? (
            <button
              className="btn btn-secondary"
              style={{ padding: '8px 14px', color: 'var(--danger)', borderColor: 'var(--danger)' }}
              onClick={() => setConfirmDeactivate(role)}
            >
              Deaktivieren
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              style={{ padding: '8px 14px' }}
              onClick={() => toggleActive(role)}
            >
              Aktivieren
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {active.map((r) => <RoleRow key={r.id} role={r} />)}

      {inactive.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Deaktiviert
          </div>
          {inactive.map((r) => <RoleRow key={r.id} role={r} />)}
        </div>
      )}

      <div style={{ marginTop: '24px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          className="form-input"
          placeholder="Name der neuen Rolle"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addRole()}
          style={{ maxWidth: '280px' }}
        />
        <button className="btn btn-primary" onClick={addRole} disabled={adding || !newName.trim()}>
          + Neue Rolle
        </button>
      </div>

      {confirmDeactivate && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setConfirmDeactivate(null)}
        >
          <div
            className="card"
            style={{ padding: '24px', maxWidth: '360px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '12px' }}>Rolle deaktivieren?</h3>
            <p style={{ color: 'var(--text-light)', marginBottom: '20px', fontSize: '0.9rem' }}>
              „{confirmDeactivate.name}" wird deaktiviert. Bestehende Zeiteinträge bleiben erhalten.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmDeactivate(null)}>Abbrechen</button>
              <button className="btn btn-danger" onClick={() => toggleActive(confirmDeactivate)}>Deaktivieren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EntriesTab (admin view of all time entries) ───────────────────────────────

function EntriesTab({ roles, users }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month: filterMonth });
      if (filterUser) params.set('user_id', filterUser);
      if (filterRole) params.set('om_role_id', filterRole);
      const res = await api.get(`/om/time-entries?${params}`);
      setEntries(res.data);
    } catch {
      toast.error('Fehler beim Laden der Einträge');
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterUser, filterRole]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  async function deleteEntry(id) {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await api.delete(`/om/time-entries/${id}`);
      toast.success('Gelöscht');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler');
    }
  }

  async function saveEdit(id) {
    try {
      await api.put(`/om/time-entries/${id}`, editDraft);
      toast.success('Gespeichert');
      setEditingId(null);
      setEditDraft({});
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <select className="form-input" value={filterUser} onChange={(e) => setFilterUser(e.target.value)} style={{ maxWidth: '180px' }}>
          <option value="">Alle Personen</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select className="form-input" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ maxWidth: '180px' }}>
          <option value="">Alle Ämter</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <input
          type="month"
          className="form-input"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={{ maxWidth: '160px' }}
        />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '24px' }}><div className="spinner" /></div>}

      {!loading && entries.length === 0 && (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>Keine Einträge gefunden.</p>
      )}

      {!loading && entries.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Amt</th>
                <th>Datum</th>
                <th>Von</th>
                <th>Bis</th>
                <th>Stunden</th>
                <th>Notiz</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => editingId === e.id ? (
                <tr key={e.id}>
                  <td><PersonName userId={e.user_id} name={e.user_name} /></td>
                  <td>
                    <select className="form-input" value={editDraft.om_role_id ?? e.om_role_id} onChange={(ev) => setEditDraft((d) => ({ ...d, om_role_id: ev.target.value }))} style={{ fontSize: '0.85rem' }}>
                      {roles.filter((r) => r.is_active).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </td>
                  <td><input type="date" className="form-input" defaultValue={e.date} onChange={(ev) => setEditDraft((d) => ({ ...d, date: ev.target.value }))} style={{ fontSize: '0.85rem' }} /></td>
                  <td><input type="time" className="form-input" defaultValue={e.start_time} onChange={(ev) => setEditDraft((d) => ({ ...d, start_time: ev.target.value }))} style={{ fontSize: '0.85rem' }} /></td>
                  <td><input type="time" className="form-input" defaultValue={e.end_time} onChange={(ev) => setEditDraft((d) => ({ ...d, end_time: ev.target.value }))} style={{ fontSize: '0.85rem' }} /></td>
                  <td>—</td>
                  <td><input className="form-input" defaultValue={e.note} onChange={(ev) => setEditDraft((d) => ({ ...d, note: ev.target.value }))} style={{ fontSize: '0.85rem' }} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.8rem', marginRight: '4px' }} onClick={() => saveEdit(e.id)}>✓</button>
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={() => { setEditingId(null); setEditDraft({}); }}>✗</button>
                  </td>
                </tr>
              ) : (
                <tr key={e.id}>
                  <td><PersonName userId={e.user_id} name={e.user_name} /></td>
                  <td>{e.role_name}</td>
                  <td>{e.date}</td>
                  <td>{e.start_time?.slice(0, 5)}</td>
                  <td>{e.end_time?.slice(0, 5)}</td>
                  <td>{fmtMins(e.total_minutes)} h</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{e.note || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem', marginRight: '4px' }} onClick={() => { setEditingId(e.id); setEditDraft({}); }}>✎</button>
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem', color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => deleteEntry(e.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── OMRolesPage ───────────────────────────────────────────────────────────────

export default function OMRolesPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!isAdmin) navigate('/roster', { replace: true });
  }, [isAdmin, navigate]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/om/roles');
      setRoles(res.data);
    } catch {
      toast.error('Fehler beim Laden der Ämter');
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    // All users, any role, shift worker or not: OM roles are often held by
    // office staff who do not work shifts (works_shifts = 0)
    api.get('/users').then((r) => setUsers(r.data || [])).catch(() => {});
  }, [fetchRoles]);

  if (!isAdmin) return null;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">OM-Ämter</h1>
          <p className="page-subtitle">Ämter verwalten und Zeiteinträge einsehen</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid var(--border)' }}>
        {[
          { key: 'roles', label: 'Ämter' },
          { key: 'entries', label: 'Zeiteinträge' },
        ].map((t) => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
            style={{ marginBottom: '-2px' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: '20px' }}>
        {tab === 'roles' && <RolesTab roles={roles} users={users} onRolesChange={fetchRoles} />}
        {tab === 'entries' && <EntriesTab roles={roles} users={users} />}
      </div>
    </div>
  );
}
